import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ENV_CONFIG } from './config/env';
import AdminGuard from './components/AdminGuard';
import Layout from './components/Layout';
import { FeatureGate } from './components/FeatureLocked';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import UsersPage from './pages/UsersPage';
import UserDetailPage from './pages/UserDetailPage';
import StudiosPage from './pages/StudiosPage';
import BrandsPage from './pages/BrandsPage';
import BrandDetailPage from './pages/BrandDetailPage';
import StudioDetailPage from './pages/StudioDetailPage';
import EventDetailPage from './pages/EventDetailPage';
import SessionDetailPage from './pages/SessionDetailPage';
import BookingsPage from './pages/BookingsPage';
import BookingDetailPage from './pages/BookingDetailPage';
import TransactionsPage from './pages/TransactionsPage';
import ClassesPage from './pages/ClassesPage';
import ClassDetailPage from './pages/ClassDetailPage';
import ActiveClassesPage from './pages/ActiveClassesPage';
import InstructorsPage from './pages/InstructorsPage';
import InstructorPerformancePage from './pages/InstructorPerformancePage';
import EventsPage from './pages/EventsPage';
import UsersMapPage from './pages/UsersMapPage';
import PromoCodesPage from './pages/PromoCodesPage';
import CouponsPage from './pages/CouponsPage';
import ReferralsPage from './pages/ReferralsPage';
import MilestonesPage from './pages/MilestonesPage';
import AdvertisementsPage from './pages/AdvertisementsPage';
import LeadsInquiriesPage from './pages/LeadsInquiriesPage';
import PacksPage from './pages/PacksPage';
import PurchasedPacksPage from './pages/PurchasedPacksPage';
import WhitelabelPage from './pages/WhitelabelPage';
import NotificationsPage from './pages/NotificationsPage';
import StudioUsersPage from './pages/StudioUsersPage';
import StudioSessionsPage from './pages/StudioSessionsPage';
import PartnerApiLogsPage from './pages/PartnerApiLogsPage';
import StudioEnquiriesPage from './pages/StudioEnquiriesPage';
import StudioCustomersPage from './pages/StudioCustomersPage';
import HealthFormsPage from './pages/HealthFormsPage';
import MerchandisePage from './pages/MerchandisePage';
import AuditLogsPage from './pages/AuditLogsPage';
import QADashboardPage from './pages/QADashboardPage';
import SessionWaitlistPage from './pages/SessionWaitlistPage';
import StudioPaymentsPage from './pages/StudioPaymentsPage';
import PurplePilatesOpsPage from './pages/PurplePilatesOpsPage';

import ReviewsPage from './pages/ReviewsPage';
import InvoiceGeneratorPage from './pages/InvoiceGeneratorPage';
import InvoicesPage from './pages/InvoicesPage';
import StudioPayoutsPage from './pages/StudioPayoutsPage';
import SeatLayoutsPage from './pages/SeatLayoutsPage';
import FeatureRequestsPage from './pages/FeatureRequestsPage';
import FeatureRequestDetailPage from './pages/FeatureRequestDetailPage';
import AdminFeatureRequestsPage from './pages/AdminFeatureRequestsPage';
import theme from './theme/theme';


const UnauthorizedPage = () => <div>Unauthorized - You don't have permission to access this page</div>;

// Studio Profile redirect component
const StudioProfilePage: React.FC = () => {
  const adminData = localStorage.getItem('admin_data');
  if (adminData) {
    try {
      const admin = JSON.parse(adminData);
      if (admin.studio?._id) {
        return <Navigate to={`/studios/${admin.studio._id}`} replace />;
      }
    } catch {}
  }
  return <Navigate to="/dashboard" replace />;
};

/** Studio owners only — super_admin uses /studio-payments instead. */
const PayoutsRouteGuard: React.FC = () => {
  const { admin } = useAuth();
  if (admin?.role === 'super_admin') {
    return <Navigate to="/studio-payments" replace />;
  }
  return <StudioPayoutsPage />;
};

/**
 * Studio OWNER only. Manager/instructor tokens are rejected outright by the
 * backend's StudioAdminOnlyGuard, so send them back to the dashboard rather
 * than a page that can only 403. Admin/super_admin have their own version of
 * this feature at /admin/feature-requests — redirect there instead of
 * showing them an empty studio-scoped list.
 *
 * Also gated on ENV_CONFIG.IS_TEST_INSTANCE — same check the TEST INSTANCE
 * badge uses — so the feature only exists on the Sankalp test instance and
 * never on the neelniketan production panel, even via a direct URL.
 */
const FeatureRequestsRouteGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { admin } = useAuth();
  if (!ENV_CONFIG.IS_TEST_INSTANCE) {
    return <Navigate to="/dashboard" replace />;
  }
  if (admin?.role === 'admin' || admin?.role === 'super_admin') {
    return <Navigate to="/admin/feature-requests" replace />;
  }
  if (admin?.role === 'manager' || admin?.role === 'instructor') {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

/** Internal admin/super_admin triage side of Feature Requests — same
 * test-instance-only gate as FeatureRequestsRouteGuard above. */
const AdminFeatureRequestsRouteGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (!ENV_CONFIG.IS_TEST_INSTANCE) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

function App() {
  // Stop the mouse wheel from nudging number inputs (price, slots, duration…)
  // up and down when the pointer is over a focused field. These fields all
  // render as <input type="number">, so one global listener covers every form.
  // Capture phase + non-passive so preventDefault() actually sticks; the value
  // and focus are left untouched, only the accidental scroll-to-change is gone.
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target instanceof HTMLInputElement &&
        target.type === 'number' &&
        target === document.activeElement
      ) {
        e.preventDefault();
      }
    };
    document.addEventListener('wheel', handleWheel, { passive: false, capture: true });
    return () => document.removeEventListener('wheel', handleWheel, { capture: true } as any);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            
            {/* Protected Routes */}
            <Route path="/" element={
              <AdminGuard>
                <Layout>
                  <Navigate to="/dashboard" replace />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/dashboard" element={
              <AdminGuard>
                <Layout>
                  <Dashboard />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/users" element={
              <AdminGuard requiredPermission="users.view">
                <Layout>
                  <UsersPage />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/users/:userId" element={
              <AdminGuard requiredPermission="users.view">
                <Layout>
                  <UserDetailPage />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/users-map" element={
              <AdminGuard requiredPermission="users.view">
                <Layout>
                  <UsersMapPage />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/studios" element={
              <AdminGuard requiredPermission="studios.view" blockStudioRoles>
                <Layout>
                  <StudiosPage />
                </Layout>
              </AdminGuard>
            } />

            <Route path="/studios/:studioId" element={
              <AdminGuard requiredPermission="studios.view" restrictStudioRoleToOwnParam="studioId">
                <Layout>
                  <StudioDetailPage />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/events/:eventId" element={
              <AdminGuard requiredPermission="events.view">
                <Layout>
                  <EventDetailPage />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/sessions/:sessionId" element={
              <AdminGuard requiredPermission="classes.view">
                <Layout>
                  <SessionDetailPage />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/classes" element={
              <AdminGuard requiredPermission="classes.view">
                <Layout>
                  <ClassesPage />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/classes/:classId" element={
              <AdminGuard requiredPermission="classes.view">
                <Layout>
                  <ClassDetailPage />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/active-classes" element={
              <AdminGuard requiredPermission="classes.view">
                <Layout>
                  <ActiveClassesPage />
                </Layout>
              </AdminGuard>
            } />

            <Route path="/studio-sessions" element={
              <AdminGuard>
                <Layout>
                  <StudioSessionsPage />
                </Layout>
              </AdminGuard>
            } />

            <Route path="/session-waitlist" element={
              <AdminGuard>
                <Layout>
                  <SessionWaitlistPage />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/events" element={
              <AdminGuard requiredPermission="events.view">
                <Layout>
                  <EventsPage />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/bookings" element={
              <AdminGuard requiredPermission="bookings.view">
                <Layout>
                  <BookingsPage />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/bookings/:bookingId" element={
              <AdminGuard requiredPermission="bookings.view">
                <Layout>
                  <BookingDetailPage />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/transactions" element={
              <AdminGuard requiredPermission="transactions.view">
                <Layout>
                  <TransactionsPage />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/instructors" element={
              <AdminGuard requiredPermission="instructors.view">
                <Layout>
                  <InstructorsPage />
                </Layout>
              </AdminGuard>
            } />

            {/* Declared AFTER /instructors so the literal path is matched first. */}
            <Route path="/instructors/:instructorId" element={
              <AdminGuard requiredPermission="instructors.view">
                <Layout>
                  <InstructorPerformancePage />
                </Layout>
              </AdminGuard>
            } />
            
            {/* Studio owners and managers see their OWN studio's reviews; the
                server scopes them by token. */}
            <Route path="/reviews" element={
              <AdminGuard>
                <Layout>
                  <ReviewsPage />
                </Layout>
              </AdminGuard>
            } />

            <Route path="/packs" element={
              <AdminGuard requiredPermission="packs.view">
                <Layout>
                  <PacksPage />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/pack-subscriptions" element={
              <AdminGuard requiredPermission="packs.view">
                <Layout>
                  <PurchasedPacksPage />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/promo-codes" element={
              <AdminGuard requiredPermission="promo_codes.view">
                <Layout>
                  <PromoCodesPage />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/coupons" element={
              <AdminGuard requiredPermission="promo_codes.view">
                <Layout>
                  <CouponsPage />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/referrals" element={
              <AdminGuard>
                <Layout>
                  <ReferralsPage />
                </Layout>
              </AdminGuard>
            } />

            <Route path="/milestones" element={
              <AdminGuard>
                <Layout>
                  <MilestonesPage />
                </Layout>
              </AdminGuard>
            } />

            <Route path="/advertisements" element={
              <AdminGuard requiredPermission="advertisements.view">
                <Layout>
                  <AdvertisementsPage />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/notifications" element={
              <AdminGuard>
                <Layout>
                  {/* Locks per channel inside the page (push vs WhatsApp). */}
                  <NotificationsPage />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/leads-inquiries" element={
              <AdminGuard requireSuperAdmin={true}>
                <Layout>
                  <LeadsInquiriesPage />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/whitelabel" element={
              <AdminGuard>
                <Layout>
                  <WhitelabelPage />
                </Layout>
              </AdminGuard>
            } />

            <Route path="/partner-api-logs" element={
              <AdminGuard requireSuperAdmin={true}>
                <Layout>
                  <PartnerApiLogsPage />
                </Layout>
              </AdminGuard>
            } />

            <Route path="/studio-enquiries" element={
              <AdminGuard requireSuperAdmin={true}>
                <Layout>
                  <StudioEnquiriesPage />
                </Layout>
              </AdminGuard>
            } />
            
            <Route path="/studio-profile" element={
              <AdminGuard>
                <Layout>
                  <StudioProfilePage />
                </Layout>
              </AdminGuard>
            } />

            <Route path="/staff" element={
              <AdminGuard>
                <Layout>
                  <StudioUsersPage />
                </Layout>
              </AdminGuard>
            } />

            <Route path="/studio-customers" element={
              <AdminGuard>
                <Layout>
                  <StudioCustomersPage />
                </Layout>
              </AdminGuard>
            } />

            <Route path="/health-forms" element={
              <AdminGuard>
                <Layout>
                  <FeatureGate path="/health-forms">
                    <HealthFormsPage />
                  </FeatureGate>
                </Layout>
              </AdminGuard>
            } />

            <Route path="/rooms" element={
              <AdminGuard>
                <Layout>
                  <SeatLayoutsPage />
                </Layout>
              </AdminGuard>
            } />

            {/* WhatsApp now lives in Engagement → WhatsApp. */}
            <Route path="/messaging" element={<Navigate to="/notifications?channel=whatsapp" replace />} />

            <Route path="/merchandise" element={
              <AdminGuard>
                <Layout>
                  <FeatureGate path="/merchandise">
                    <MerchandisePage />
                  </FeatureGate>
                </Layout>
              </AdminGuard>
            } />

            <Route path="/feature-requests" element={
              <AdminGuard>
                <Layout>
                  <FeatureRequestsRouteGuard>
                    <FeatureRequestsPage />
                  </FeatureRequestsRouteGuard>
                </Layout>
              </AdminGuard>
            } />

            <Route path="/feature-requests/:id" element={
              <AdminGuard>
                <Layout>
                  <FeatureRequestsRouteGuard>
                    <FeatureRequestDetailPage />
                  </FeatureRequestsRouteGuard>
                </Layout>
              </AdminGuard>
            } />

            <Route path="/admin/feature-requests" element={
              <AdminGuard blockStudioRoles>
                <Layout>
                  <AdminFeatureRequestsRouteGuard>
                    <AdminFeatureRequestsPage />
                  </AdminFeatureRequestsRouteGuard>
                </Layout>
              </AdminGuard>
            } />

            <Route path="/audit-logs" element={
              <AdminGuard>
                <Layout>
                  <AuditLogsPage />
                </Layout>
              </AdminGuard>
            } />

            <Route path="/qa-dashboard" element={
              <AdminGuard requireSuperAdmin={true}>
                <Layout>
                  <QADashboardPage />
                </Layout>
              </AdminGuard>
            } />

            <Route path="/studio-payments" element={
              <AdminGuard requireSuperAdmin={true}>
                <Layout>
                  <StudioPaymentsPage />
                </Layout>
              </AdminGuard>
            } />

            {/*
              Brands are the tenancy boundary itself, so creating one, moving a
              studio into one, or granting access is super-admin only. A bug in
              a self-service version of these screens is a cross-tenant breach,
              not a bad edit.
            */}
            <Route path="/brands" element={
              <AdminGuard requireSuperAdmin={true}>
                <Layout>
                  <BrandsPage />
                </Layout>
              </AdminGuard>
            } />

            <Route path="/brands/:brandId" element={
              <AdminGuard requireSuperAdmin={true}>
                <Layout>
                  <BrandDetailPage />
                </Layout>
              </AdminGuard>
            } />

            <Route path="/studio-ops" element={
              <AdminGuard requireSuperAdmin={true}>
                <Layout>
                  <PurplePilatesOpsPage />
                </Layout>
              </AdminGuard>
            } />

            <Route path="/invoice-generator" element={
              <AdminGuard>
                <Layout>
                  <InvoiceGeneratorPage />
                </Layout>
              </AdminGuard>
            } />

            <Route path="/invoice-generator/:id" element={
              <AdminGuard>
                <Layout>
                  <InvoiceGeneratorPage />
                </Layout>
              </AdminGuard>
            } />

            <Route path="/invoices" element={
              <AdminGuard>
                <Layout>
                  <InvoicesPage />
                </Layout>
              </AdminGuard>
            } />

            <Route path="/payouts" element={
              <AdminGuard>
                <Layout>
                  <PayoutsRouteGuard />
                </Layout>
              </AdminGuard>
            } />

            {/* Error Routes */}
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
